import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GenericFormField } from "common/Components/GenericForm";
import {
  buildCustomerSelectOptionsForRow,
  isCustomerAccessRowDeactivated,
  PLATFORM_USER_ACCESS_UI,
} from "../PlatformUserAccess.constants";
import {
  CustomerAccessBorderedPanel,
  CustomerAccessColumnHeaderLabel,
  CustomerAccessColumnHeaderRow,
  CustomerAccessDescription,
  CustomerAccessDeactivatedWarning,
  CustomerAccessEditorContainer,
  CustomerAccessEditorHeader,
  CustomerAccessCustomerFieldCell,
  CustomerAccessCustomerHeaderCell,
  CustomerAccessRolesFieldCell,
  CustomerAccessRolesHeaderCell,
  CustomerAccessRemoveCell,
  CustomerAccessRemoveHeaderCell,
  CustomerAccessRemoveIconButton,
  CustomerAccessRow,
  CustomerAccessRowInner,
  CustomerAccessRows,
  CustomerAccessRowsScrollArea,
  CustomerAccessAccessStatusCell,
  CustomerAccessStatusHeaderCell,
} from "./CustomerAccessEditor.styles";
import { AccessStatusChip } from "common/Components/GenericForm/FieldTypes/AccessStatusSelect";
import {
  TableRowActionCell,
  TableRowActionLink,
} from "./UserAccessList.styles";
import { getCustomerRowAction } from "../../Admin/Users/Users.constants";
const getCustomerField = (customerOptions) => ({
  name: "customerId",
  label: PLATFORM_USER_ACCESS_UI.tableColumns.customer,
  placeholder: PLATFORM_USER_ACCESS_UI.customerAccessCustomerSelectPlaceholder,
  type: "singleSelect",
  options: customerOptions,
  compact: true,
  inline: true,
  disableClearable: true,
});

const getRolesField = (roleOptions, disabled = false) => ({
  name: "roles",
  label: PLATFORM_USER_ACCESS_UI.tableColumns.roles,
  type: "multiSelect",
  options: roleOptions,
  compact: true,
  inline: true,
  valueDisplay: "comma",
  disabled,
});

const hasPersistedAccessId = (row) => {
  const accessId = row?.accessId;
  return accessId != null && String(accessId).trim() !== "";
};

const CustomerAccessEditor = ({
  rows,
  customerOptions,
  activeCustomers = [],
  roleOptions,
  userId,
  onAddRow,
  onRemoveRow,
  onRowChange,
  title = PLATFORM_USER_ACCESS_UI.customerAccessTitle,
  showDescription = false,
  showColumnHeaders = true,
  disabled = false,
  hideRemoveForPersistedRows = false,
  rowErrors = [],
  scrollableRows = false,
  onAccessActionSuccess,
  onCustomerUserListAction,
}) => {
  const handleFieldChange = (rowIndex) => (name, value) => {
    onRowChange(rowIndex, name, value);
  };
  const onRowAction = async (row, action) => {
    if (!onCustomerUserListAction) {
      return;
    }
    const result = await onCustomerUserListAction(
      row.customerId,
      row.customerCode,
      { ...row, userId },
      action,
      row.roles,
      true
    );
    if (result?.ok) {
      await onAccessActionSuccess?.();
    }
  };
  const handleRowAction = (row, isDeactivated) => {
    if (isDeactivated) {
      return null;
    }
    const rowAction = getCustomerRowAction(row.accessStatus);
    if (!rowAction || !onCustomerUserListAction) {
      return null;
    }
    return (
      <TableRowActionCell onClick={(event) => event.stopPropagation()}>
        <TableRowActionLink
          type="button"
          onClick={() => onRowAction(row, rowAction.action)}
        >
          {rowAction.label}
        </TableRowActionLink>
      </TableRowActionCell>
    );
  };

  const rowsContent = (
    <CustomerAccessRows $inScrollArea={scrollableRows}>
      {rows.map((row, index) => {
        const errors = rowErrors[index] ?? {};
        const isDeactivated = isCustomerAccessRowDeactivated(
          row,
          activeCustomers
        );
        const showRemoveButton =
          !disabled &&
          !isDeactivated &&
          !(hideRemoveForPersistedRows && hasPersistedAccessId(row));
        return (
          <CustomerAccessRow key={`customer-access-${index}`}>
            <CustomerAccessRowInner>
              <CustomerAccessCustomerFieldCell>
                <GenericFormField
                  field={{
                    ...getCustomerField(
                      buildCustomerSelectOptionsForRow(
                        customerOptions,
                        rows,
                        index
                      )
                    ),
                    disabled: disabled || hasPersistedAccessId(row),
                  }}
                  value={row.customerId}
                  allValues={row}
                  onChange={handleFieldChange(index)}
                  error={errors.customerId}
                />
              </CustomerAccessCustomerFieldCell>
              <CustomerAccessRolesFieldCell>
                <GenericFormField
                  field={getRolesField(
                    roleOptions,
                    disabled || isDeactivated || !row.customerId
                  )}
                  value={row.roles}
                  allValues={row}
                  onChange={handleFieldChange(index)}
                  error={errors.roles}
                />
              </CustomerAccessRolesFieldCell>
              <CustomerAccessAccessStatusCell>
                {row.accessStatus ? (
                  <AccessStatusChip status={row.accessStatus} />
                ) : null}
              </CustomerAccessAccessStatusCell>
              <CustomerAccessRemoveCell>
                {showRemoveButton ? (
                  <CustomerAccessRemoveIconButton
                    size="small"
                    aria-label={
                      PLATFORM_USER_ACCESS_UI.removeCustomerRowAriaLabel
                    }
                    onClick={() => onRemoveRow(index)}
                  >
                    <CloseIcon fontSize="small" />
                  </CustomerAccessRemoveIconButton>
                ) : (
                  handleRowAction(row, isDeactivated)
                )}
              </CustomerAccessRemoveCell>
            </CustomerAccessRowInner>
            {isDeactivated ? (
              <CustomerAccessDeactivatedWarning>
                {PLATFORM_USER_ACCESS_UI.customerDeactivatedWarning}
              </CustomerAccessDeactivatedWarning>
            ) : null}
          </CustomerAccessRow>
        );
      })}
    </CustomerAccessRows>
  );

  return (
    <CustomerAccessEditorContainer $fillHeight={scrollableRows}>
      <CustomerAccessEditorHeader
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h6">{title}</Typography>
          {showDescription ? (
            <CustomerAccessDescription variant="body2">
              {PLATFORM_USER_ACCESS_UI.customerAccessDescription}
            </CustomerAccessDescription>
          ) : null}
        </Box>
        <Button
          onClick={onAddRow}
          variant="outlined"
          size="small"
          disabled={disabled}
        >
          {PLATFORM_USER_ACCESS_UI.addCustomerLabel}
        </Button>
      </CustomerAccessEditorHeader>
      <CustomerAccessBorderedPanel $fillHeight={scrollableRows}>
        {showColumnHeaders ? (
          <CustomerAccessColumnHeaderRow>
            <CustomerAccessCustomerHeaderCell>
              <CustomerAccessColumnHeaderLabel variant="caption">
                {PLATFORM_USER_ACCESS_UI.tableColumns.customer}
              </CustomerAccessColumnHeaderLabel>
            </CustomerAccessCustomerHeaderCell>
            <CustomerAccessRolesHeaderCell>
              <CustomerAccessColumnHeaderLabel variant="caption">
                {PLATFORM_USER_ACCESS_UI.tableColumns.roles}
              </CustomerAccessColumnHeaderLabel>
            </CustomerAccessRolesHeaderCell>
            <CustomerAccessStatusHeaderCell>
              <CustomerAccessColumnHeaderLabel variant="caption">
                {PLATFORM_USER_ACCESS_UI.tableColumns.customerAccess}
              </CustomerAccessColumnHeaderLabel>
            </CustomerAccessStatusHeaderCell>
            <CustomerAccessRemoveHeaderCell>
              <CustomerAccessColumnHeaderLabel variant="caption">
                {PLATFORM_USER_ACCESS_UI.actionColumnLabel}
              </CustomerAccessColumnHeaderLabel>
            </CustomerAccessRemoveHeaderCell>
          </CustomerAccessColumnHeaderRow>
        ) : null}
        {scrollableRows ? (
          <CustomerAccessRowsScrollArea>{rowsContent}</CustomerAccessRowsScrollArea>
        ) : (
          rowsContent
        )}
      </CustomerAccessBorderedPanel>
    </CustomerAccessEditorContainer>
  );
};

CustomerAccessEditor.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  customerOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeCustomers: PropTypes.arrayOf(PropTypes.object),
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAddRow: PropTypes.func.isRequired,
  onRemoveRow: PropTypes.func.isRequired,
  onRowChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  showDescription: PropTypes.bool,
  showColumnHeaders: PropTypes.bool,
  disabled: PropTypes.bool,
  hideRemoveForPersistedRows: PropTypes.bool,
  rowErrors: PropTypes.arrayOf(PropTypes.object),
  scrollableRows: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAccessActionSuccess: PropTypes.func,
  onCustomerUserListAction: PropTypes.func,
};

CustomerAccessEditor.defaultProps = {
  disabled: false,
  activeCustomers: [],
};

export default CustomerAccessEditor;
